import { useEffect, useRef, useState, type ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SchoolDetailsFields } from "@/components/school-details-fields";
import { StudentLoginFields } from "@/components/students/student-login-fields";
import {
  EXERCISE_COUNT_MIN,
  EXERCISE_COUNT_MAX,
  EXERCISE_COUNT_HINT,
  subjectExerciseCountField,
} from "@/lib/students";

export type StudentFormDefaults = {
  name?: string | null;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  firstGradeYear?: number | null;
  email?: string | null;
  hasPassword?: boolean | null;
  notes?: string | null;
  defaultExerciseCount?: number | null;
  /** What a session holds today when this learner has no number of their own. */
  suggestedExerciseCount?: number | null;
  /**
   * Session length per subject. Absent — as it is on the create page — means the
   * per-subject rows are not drawn at all: they are about what a learner
   * studies, which is not a thing anybody knows in the moment they are adding
   * them.
   */
  subjectSessionLengths?: SubjectSessionLength[] | null;
  /** Must a wrong answer be talked through with the tutor before finishing? */
  requireDiscussionResolution?: boolean | null;
};

export type SubjectSessionLength = {
  subjectKey: string;
  subjectName: string;
  /** What a grown-up set, or null when they set nothing. */
  count?: number | null;
  /** What a session in this subject would be right now, limit or not. */
  effectiveCount: number;
};

type FormTab = "profile" | "login" | "sessions";

/**
 * A tab's own short sentence, then its fields.
 *
 * This replaces the left-hand label column the sections used to have: the tab
 * already names the group, so a second heading beside the fields was the same
 * word twice, and the 13rem column was most of why the form felt crowded.
 */
function TabPanel({
  value,
  intro,
  children,
}: {
  value: FormTab;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    // forceMount: the form is read with FormData on submit, and Radix unmounts an
    // inactive tab — which would drop its fields from the save, and the edit page
    // reads an absent field as "clear it". So every tab stays mounted and the
    // inactive ones are only hidden.
    <TabsContent
      value={value}
      forceMount
      data-form-tab={value}
      className="space-y-5 px-6 py-6 data-[state=inactive]:hidden"
    >
      {intro && <p className="text-sm text-muted-foreground">{intro}</p>}
      {children}
    </TabsContent>
  );
}

/**
 * Every field of the learner form, on three tabs.
 *
 * Shared by creating and editing rather than written twice: the two pages differ
 * only in their heading, their submit button and where „Atšaukti" goes, and every
 * time a field was added before it had to be added in both — which is how they
 * drifted (the edit page's exercise-count placeholder named a number the create
 * page could not know).
 *
 * Tabs rather than one long page because the three groups are three different
 * errands — who the child is, how they sign in, how their sessions run — and an
 * edit is almost always one of them. One form and one „Išsaugoti" still cover
 * all three, so switching tabs never loses anything typed.
 */
export function StudentFormFields({
  defaults = {},
  disabled,
}: {
  defaults?: StudentFormDefaults;
  disabled?: boolean;
}) {
  const suggested = defaults.suggestedExerciseCount ?? null;
  const subjectLengths = defaults.subjectSessionLengths ?? [];
  const [tab, setTab] = useState<FormTab>("profile");
  const root = useRef<HTMLDivElement>(null);

  // A field the browser refuses on submit (an emptied name, a seven-letter
  // password) may sit on a hidden tab, where the browser cannot focus it — so
  // the save would silently do nothing. Bring its tab forward and show the
  // message there. `invalid` does not bubble, hence a native capture listener
  // rather than a React prop on this wrapper.
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    let pending = false;
    const onInvalid = (event: Event) => {
      if (pending) return; // one submit fires this once per invalid field
      const field = event.target as HTMLInputElement;
      const owner = field.closest<HTMLElement>("[data-form-tab]")?.dataset.formTab as
        | FormTab
        | undefined;
      if (!owner || owner === tab) return;
      pending = true;
      setTab(owner);
      requestAnimationFrame(() => {
        pending = false;
        field.reportValidity();
      });
    };
    node.addEventListener("invalid", onInvalid, true);
    return () => node.removeEventListener("invalid", onInvalid, true);
  }, [tab]);

  return (
    <div ref={root}>
      <Tabs value={tab} onValueChange={(value) => setTab(value as FormTab)} className="gap-0">
        <div className="border-b px-6 pt-5 pb-4">
          <TabsList className="w-full sm:w-fit">
            <TabsTrigger value="profile" className="sm:px-4">Profilis</TabsTrigger>
            <TabsTrigger value="login" className="sm:px-4">Prisijungimas</TabsTrigger>
            <TabsTrigger value="sessions" className="sm:px-4">Sesijos</TabsTrigger>
          </TabsList>
        </div>

        {/* No intro sentence: the birth-date fields carry their own hint and
            end in a summary line saying the same thing, so a third copy at the
            top was the crowding this redesign is removing. */}
        <TabPanel value="profile">
          <div className="space-y-2">
            <Label htmlFor="name">Vardas *</Label>
            <Input
              id="name"
              name="name"
              className="max-w-sm"
              placeholder="Mokinio vardas"
              defaultValue={defaults.name ?? ""}
              required
              disabled={disabled}
            />
          </div>

          {/* Stored as `notes`, and it was labelled „Pastabos — tik jums" — which
              was not true: it is printed under the learner's name on their card
              and their page. So it is named for what it is, a line under the
              name, and kept to one line. */}
          <div className="space-y-2">
            <Label htmlFor="notes">Paantraštė</Label>
            <Input
              id="notes"
              name="notes"
              maxLength={120}
              className="max-w-md"
              placeholder="Pvz.: Mėgsta paspirtukus ir dinozaurus"
              defaultValue={defaults.notes ?? ""}
              disabled={disabled}
            />
            <p className="text-xs text-muted-foreground">
              Trumpas tekstas po vardu mokinio kortelėje ir puslapyje.
            </p>
          </div>

          <SchoolDetailsFields
            birthYear={defaults.birthYear}
            birthMonth={defaults.birthMonth}
            birthDay={defaults.birthDay}
            firstGradeYear={defaults.firstGradeYear}
            disabled={disabled}
          />
        </TabPanel>

        <TabPanel
          value="login"
          intro="Neprivaloma. Su šiais duomenimis mokinys prisijungs pats — arba per Google, jei tuo pačiu el. paštu turi Google paskyrą."
        >
          <StudentLoginFields
            disabled={disabled}
            defaultEmail={defaults.email}
            hasPassword={defaults.hasPassword ?? false}
          />
        </TabPanel>

        <TabPanel value="sessions">
          <div className="space-y-2">
            <Label htmlFor="defaultExerciseCount">Užduočių skaičius sesijoje</Label>
            <Input
              id="defaultExerciseCount"
              name="defaultExerciseCount"
              type="number"
              className="w-24"
              min={EXERCISE_COUNT_MIN}
              max={EXERCISE_COUNT_MAX}
              defaultValue={defaults.defaultExerciseCount ?? ""}
              // Empty means "whatever the app-wide setting says", so the field
              // shows that number rather than leaving a grown-up to guess what
              // they are opting out of.
              placeholder={suggested ? `${suggested}` : "Bendras"}
              disabled={disabled}
            />
            <p className="text-xs text-muted-foreground">{EXERCISE_COUNT_HINT}</p>
          </div>

          {subjectLengths.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="text-sm font-medium leading-none">Riba pagal dalyką</h3>
                <p className="text-xs text-muted-foreground">
                  Neprivaloma. Mokinys gali pasirinkti trumpesnę sesiją, bet ne ilgesnę.
                </p>
              </div>

              <div className="space-y-2">
                {subjectLengths.map((subject) => {
                  const field = subjectExerciseCountField(subject.subjectKey);

                  return (
                    <div key={subject.subjectKey} className="flex flex-wrap items-center gap-3">
                      <Label htmlFor={field} className="w-32 shrink-0 font-normal">
                        {subject.subjectName}
                      </Label>
                      <Input
                        id={field}
                        name={field}
                        type="number"
                        className="w-24"
                        min={EXERCISE_COUNT_MIN}
                        max={EXERCISE_COUNT_MAX}
                        defaultValue={subject.count ?? ""}
                        // The same reasoning as the field above: an empty box is
                        // "no limit here", so it shows the length this subject
                        // would get anyway rather than looking like a zero.
                        placeholder={`${subject.effectiveCount}`}
                        disabled={disabled}
                      />
                      {/* No trailing note saying "no limit — 20 questions": it is
                          derived from what was saved, so the moment a number is
                          typed it stands beside the box contradicting it — and
                          unset it only repeats the placeholder. The greyed
                          placeholder is the whole signal, and it is the one that
                          cannot go stale, because the browser hides it as soon as
                          anything is typed. */}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* The app's standing answer is that an unfinished conversation is a
              note and never a gate — a refusal cannot teach the thing it is
              refusing for. This is the exception a grown-up may make for one
              child: the one who closes the tutor the moment it opens, and for
              whom the conversation is the whole of what a wrong answer is worth.

              The description says what it costs and what it does not, because
              „force" is the word that would make somebody expect a child stuck
              until they get it right — which is precisely what this does not do. */}
          <div className="flex items-start gap-3 border-t pt-5">
            <Checkbox
              id="requireDiscussionResolution"
              name="requireDiscussionResolution"
              defaultChecked={defaults.requireDiscussionResolution ?? false}
              disabled={disabled}
            />
            <div className="space-y-1">
              <Label htmlFor="requireDiscussionResolution" className="font-medium">
                Klaidingus atsakymus aptarti su AI asistentu
              </Label>
              <p className="text-sm text-muted-foreground">
                Sesijos nebus galima baigti, kol mokinys neatsakys pokalbyje apie kiekvieną
                klaidingą atsakymą. Teisingai atsakyti nereikia — užtenka parašyti, kaip galvojo.
              </p>
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}
